const isCheckboxField = (field) => {
    const type = field?.attrs?.type;
    return type === "checkbox" || field?.tag === "checkbox";
};

const getCheckboxOtherFields = (schema) => {
    if (!schema?.fields) return [];

    return Object.values(schema.fields)
        .filter((field) => isCheckboxField(field) && field?.config?.otherOption)
        .map((field) => ({
            name: field?.attrs?.name || field?.config?.sys?.full_name,
            label: field?.config?.otherLabel || "Other",
            placeholder: field?.config?.otherPlaceholder || "Other",
        }))
        .filter((field) => field.name);
};

const isRequiredField = (field) => {
    return Boolean(
        field?.attrs?.required ||
        field?.config?.required ||
        field?.config?.validation?.required
    );
};

const applyCheckboxRequiredAttributes = (container, schema) => {
    if (!container || !schema?.fields) return;

    Object.values(schema.fields).forEach((field) => {
        if (!isCheckboxField(field)) return;
        if (!isRequiredField(field)) return;

        const schemaName =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        const fieldId = field.id;
        if (!schemaName && !fieldId) return;

        const checkboxes = container.querySelectorAll(
            [
                schemaName
                    ? `input[type="checkbox"][name="${CSS.escape(schemaName)}"]`
                    : null,
                fieldId
                    ? `input[type="checkbox"][name="f-${fieldId}"]`
                    : null,
            ]
                .filter(Boolean)
                .join(", ")
        );

        if (!checkboxes.length) return;

        checkboxes.forEach((el, idx) => {
            el.setAttribute("aria-required", "true");
            el.dataset.requiredGroup = "true";
            if (idx === 0) {
                el.setAttribute("required", "");
            } else {
                el.removeAttribute("required");
            }
        });
    });
};

const getFormeoControl = (input) => {
    return input?.closest(".formeo-control") || input?.parentElement;
};

const ensureOtherOption = (control, { name, label, placeholder }) => {
    if (control.querySelector(`[data-other-for="${name}"]`)) return;

    const wrapper = document.createElement("div");
    wrapper.className = "formeo-other-option mt-2 flex items-center gap-2 w-full";
    wrapper.dataset.otherFor = name;

    const toggleId = `${name}__other_toggle`;
    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.className = "checkbox checkbox-sm";
    toggle.id = toggleId;
    toggle.setAttribute("aria-label", label);

    const textLabel = document.createElement("label");
    textLabel.className = "checkbox-label select-none";
    textLabel.htmlFor = toggleId;
    textLabel.textContent = label;

    const input = document.createElement("input");
    input.type = "text";
    input.name = `${name}__other`;
    input.className = "input input-sm";
    input.placeholder = placeholder;
    input.disabled = true;

    toggle.addEventListener("change", () => {
        input.disabled = !toggle.checked;
        if (!toggle.checked) input.value = "";
    });

    wrapper.append(toggle, textLabel, input);
    control.appendChild(wrapper);
};

const injectCheckboxOtherOptions = (container, schema) => {
    if (!container || !schema?.fields) return;

    Object.values(schema.fields).forEach((field) => {
        if (!field?.config?.otherOption) return;
        if (!isCheckboxField(field)) return;

        const schemaName =
            field?.attrs?.name ||
            field?.config?.sys?.full_name;

        const fieldId = field.id;

        if (!schemaName && !fieldId) return;

        // 🔥 Support BOTH Formeo render styles
        const checkboxes = container.querySelectorAll(
            [
                // single checkbox
                schemaName
                    ? `input[type="checkbox"][name="${CSS.escape(schemaName)}"]`
                    : null,

                // multi checkbox
                fieldId
                    ? `input[type="checkbox"][name="f-${fieldId}"]`
                    : null,
            ]
                .filter(Boolean)
                .join(", ")
        );

        if (!checkboxes.length) return;

        // resolve control
        const control =
            checkboxes[0].closest(".formeo-column") ||
            getFormeoControl(checkboxes[0]);

        if (!control) return;

        const key = schemaName || `f-${fieldId}`;

        // prevent duplicate
        if (control.querySelector(`[data-other-for="${key}"]`)) return;

        /* ===== Render Other toggle + input ===== */

        const wrapper = document.createElement("div");
        wrapper.className = "formeo-other-option mt-2 flex items-center gap-2 w-full";
        wrapper.dataset.otherFor = key;

        const toggleId = `${key}__other_toggle`;

        const toggle = document.createElement("input");
        toggle.type = "checkbox";
        toggle.className = "checkbox checkbox-sm";
        toggle.id = toggleId;

        const label = document.createElement("label");
        label.htmlFor = toggleId;
        label.className = "checkbox-label select-none";
        label.textContent = field.config?.otherLabel || "Other";

        const input = document.createElement("input");
        input.type = "text";
        input.name = `${schemaName}__other`;
        input.className = "input input-sm";
        input.placeholder = field.config?.otherPlaceholder || "Other";
        input.disabled = true;

        toggle.addEventListener("change", () => {
            input.disabled = !toggle.checked;
            if (!toggle.checked) input.value = "";
        });

        wrapper.append(toggle, label, input);

        // attach after checkbox or group
        let anchor = null;

        // 🔹 SINGLE checkbox → insert after its label
        if (checkboxes.length === 1) {
            const checkbox = checkboxes[0];
            anchor =
                container.querySelector(`label[for="${checkbox.id}"]`) ||
                checkbox;
        }

        // 🔹 MULTI checkbox → insert after last option
        if (checkboxes.length > 1) {
            anchor = checkboxes[checkboxes.length - 1].closest(".f-checkbox");
        }

        if (!anchor) return;

        anchor.after(wrapper);
    });
};


export { injectCheckboxOtherOptions, applyCheckboxRequiredAttributes };
